/**
 * Modale « détail d'un club » au CLIC sur une ligne du classement des clubs : le
 * détail PAR JOUEUR des internationaux de ce club au Mondial (note de tournoi,
 * buts, passes décisives, matchs joués). Table à tri deux niveaux (cf. tableSort.ts) ;
 * le nom ouvre la fiche joueur. Données déjà en mémoire (roster servi dans
 * leaderboards.json) → affichage instantané, sans requête. Réutilise `.md-overlay`.
 */
import type { ClubRating } from "../app/leaderboards.ts";
import { esc, flagCode, frontZ, playerLink } from "./html.ts";
import { track } from "../app/track.ts";
import { createSortState, cycleSort, sortRows, sortClass, sortMark, sortAria } from "./components/tableSort.ts";

type Key = "note" | "goals" | "assists" | "apps";
const sort = createSortState<Key>("note");
let current: ClubRating | null = null;

const metric = (p: ClubRating["roster"][number], k: Key): number =>
  k === "goals" ? p.goals : k === "assists" ? p.assists : k === "apps" ? p.apps : p.rating;

/** Corps de la modale (PUR → testé) : en-tête club + table triable par joueur. */
export function clubDetailBodyHtml(club: ClubRating, s = sort): string {
  const logo = club.clubLogo
    ? `<img class="cd-logo" src="${esc(club.clubLogo)}" alt="" width="34" height="34">`
    : `<span class="cd-logo cr-logo-ph" aria-hidden="true"><i class="ti ti-shield"></i></span>`;
  const head = `<div class="sv-head">
      ${logo}
      <div class="sv-id">
        <div class="sv-name">${esc(club.club)}</div>
        <div class="sv-sub">${club.players} joueur${club.players > 1 ? "s" : ""} au Mondial · note moy. <b>${club.rating.toFixed(2)}</b> · ${club.goals} but${club.goals > 1 ? "s" : ""} · ${club.assists} passe${club.assists > 1 ? "s" : ""} D</div>
      </div>
    </div>`;
  const th = (k: Key, label: string): string => {
    const cls = sortClass(s, k);
    return `<th class="cr-th-sort${cls ? ` ${cls}` : ""}" data-sort="${k}" role="button" tabindex="0" aria-sort="${sortAria(s, k)}">${label}${sortMark(s, k)}</th>`;
  };
  const rows = sortRows(club.roster, s, metric, (a, b) =>
    (a.name ?? "").localeCompare(b.name ?? ""),
  )
    .map((p, i) => {
      const flag = p.teamCode
        ? `<span class="f fi fi-${esc(flagCode(p.teamCode))} cd-flag" title="${esc(p.teamCode)}"></span>`
        : "";
      const note = p.rating.toFixed(1);
      return `<tr class="cd-row">
        <td class="cr-rank">${i + 1}</td>
        <td class="cd-pl">${flag}<span class="cd-pl-name">${playerLink(p.name ?? "—", p.id)}</span></td>
        <td class="cd-note"><b>${note}</b></td>
        <td class="cr-stat">${p.goals}</td>
        <td class="cr-stat">${p.assists}</td>
        <td class="cr-stat">${p.apps}</td>
      </tr>`;
    })
    .join("");
  return `${head}
    <table class="cr-table cd-table">
      <thead><tr>
        <th class="cr-th-rank">#</th>
        <th class="cd-th-pl">Joueur</th>
        ${th("note", "Note")}
        ${th("goals", "Buts")}
        ${th("assists", "Passes D")}
        ${th("apps", "Matchs")}
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <p class="note sv-foot">Note moyenne par joueur sur ses matchs de Coupe du monde · buts / passes décisives / matchs joués au Mondial. Clique un en-tête pour trier (¹ primaire, ² secondaire) ou un nom pour la fiche joueur.</p>`;
}

// --- contrôleur overlay (réutilise .md-overlay) -----------------------------
let overlay: HTMLElement | null = null;
let open = false;

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  const ov = document.createElement("div");
  ov.className = "md-overlay sv-overlay cd-overlay hide";
  ov.innerHTML = `<div class="md-card sv-card cd-card" role="dialog" aria-modal="true" aria-label="Détail du club par joueur"><button type="button" class="md-close" aria-label="Fermer"><i class="ti ti-x" aria-hidden="true"></i></button><div class="md-body sv-body"></div></div>`;
  ov.addEventListener("click", (e) => {
    if (e.target === ov) close();
  });
  ov.querySelector(".md-close")?.addEventListener("click", () => close());
  // Tri : clic / clavier sur un en-tête → bascule + re-rend le corps.
  const body = ov.querySelector<HTMLElement>(".sv-body");
  const onSort = (target: HTMLElement | null): void => {
    const key = target?.closest<HTMLElement>("[data-sort]")?.dataset.sort as Key | undefined;
    if (!key || !current || !body) return;
    cycleSort(sort, key);
    body.innerHTML = clubDetailBodyHtml(current);
  };
  ov.addEventListener("click", (e) => onSort(e.target as HTMLElement));
  ov.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      const t = e.target as HTMLElement;
      if (t.closest("[data-sort]")) {
        e.preventDefault();
        onSort(t);
      }
    }
  });
  // Échap ferme (ajouté ici → pas d'accès `document` au chargement du module).
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
  });
  overlay = ov;
  document.body.appendChild(ov);
  return ov;
}

/** Ouvre la modale détail d'un club (données fournies, aucune requête). */
export function openClubDetail(club: ClubRating): void {
  current = club;
  track("fiche", { kind: "club", code: club.club });
  const ov = ensureOverlay();
  const body = ov.querySelector<HTMLElement>(".sv-body");
  if (!body) return;
  body.innerHTML = clubDetailBodyHtml(club);
  body.scrollTop = 0;
  ov.classList.remove("hide");
  ov.style.zIndex = String(frontZ());
  open = true;
  requestAnimationFrame(() => ov.classList.add("open"));
}

function close(): void {
  if (!overlay || !open) return;
  open = false;
  overlay.classList.remove("open");
  const ov = overlay;
  setTimeout(() => {
    if (!open) ov.classList.add("hide");
  }, 300);
}
